import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as api from '../services/api';
import { clearFieldCampaignsExploreEngagedFromSession, clearCampaignWorkspaceResumeIdFromSession } from '../utils/fieldCampaignsExploreSession';
import { clearSessionRecentSearches, hydrateRecentSearchesFromServer } from '../utils/recentSearchStorage';

function onAuthenticatedSessionReady(loadSavedPlaces: () => Promise<void>) {
  void loadSavedPlaces();
  void hydrateRecentSearchesFromServer();
}

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  phone?: string;
  tier?: string;
  two_factor_enabled?: boolean;
}

export interface SavedPlace {
  id: string;
  placeId: string;
  name: string;
  address: string;
  label: 'home' | 'work' | 'saved';
  coordinates: { lat: number; lng: number };
}

interface AuthStore {
  // Auth state
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Modal state
  showAuthModal: boolean;
  authModalMode: 'login' | 'signup';
  authRedirectAction: (() => void) | null;

  // 2FA login state
  requires2FA: boolean;
  twoFactorToken: string | null;

  // Saved places
  savedPlaces: SavedPlace[];

  // Actions
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; requires2FA?: boolean }>;
  verify2FALogin: (code: string) => Promise<{ success: boolean; error?: string }>;
  signup: (name: string, email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  handleOAuthCallback: () => Promise<boolean>;

  openAuthModal: (mode: 'login' | 'signup', redirectAction?: () => void) => void;
  closeAuthModal: () => void;
  setAuthModalMode: (mode: 'login' | 'signup') => void;
  reset2FAState: () => void;

  addSavedPlace: (place: Omit<SavedPlace, 'id'>) => void;
  removeSavedPlace: (id: string) => Promise<void>;
  updateSavedPlace: (id: string, updates: Partial<SavedPlace>) => void;

  requireAuth: (action: () => void) => boolean;
  loadSavedPlaces: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      showAuthModal: false,
      authModalMode: 'login',
      authRedirectAction: null,

      // 2FA login state
      requires2FA: false,
      twoFactorToken: null,

      savedPlaces: [],

      // Actions
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.login(email, password);

          // Check if 2FA is required
          if (response.requires_2fa && response.two_factor_token) {
            set({
              isLoading: false,
              requires2FA: true,
              twoFactorToken: response.two_factor_token,
            });
            return { success: true, requires2FA: true };
          }

          if (!response.user) {
            throw new Error('Login failed. Please try again.');
          }

          const user: User = {
            id: response.user.id,
            name: response.user.name,
            email: response.user.email,
            tier: response.user.tier,
          };

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            showAuthModal: false,
            error: null,
            requires2FA: false,
            twoFactorToken: null,
          });

          onAuthenticatedSessionReady(get().loadSavedPlaces);

          // Execute redirect action if any
          const redirectAction = get().authRedirectAction;
          if (redirectAction) {
            redirectAction();
            set({ authRedirectAction: null });
          }

          return { success: true };
        } catch (error) {
          const message = error instanceof api.APIError
            ? error.message
            : 'Login failed. Please try again.';
          set({ isLoading: false, error: message });
          return { success: false, error: message };
        }
      },

      verify2FALogin: async (code: string) => {
        const { twoFactorToken } = get();
        if (!twoFactorToken) {
          return { success: false, error: 'No 2FA session. Please login again.' };
        }

        set({ isLoading: true, error: null });

        try {
          const response = await api.verify2FALogin(twoFactorToken, code);

          if (!response.user) {
            throw new Error('Verification failed. Please try again.');
          }

          const user: User = {
            id: response.user.id,
            name: response.user.name,
            email: response.user.email,
            tier: response.user.tier,
            two_factor_enabled: true,
          };

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            showAuthModal: false,
            error: null,
            requires2FA: false,
            twoFactorToken: null,
          });

          onAuthenticatedSessionReady(get().loadSavedPlaces);

          const redirectAction = get().authRedirectAction;
          if (redirectAction) {
            redirectAction();
            set({ authRedirectAction: null });
          }

          return { success: true };
        } catch (error) {
          const message = error instanceof api.APIError
            ? error.message
            : 'Invalid verification code';
          set({ isLoading: false, error: message });
          return { success: false, error: message };
        }
      },

      signup: async (name: string, email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          // Split name into first and last name
          const nameParts = name.trim().split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          await api.register({
            email,
            password,
            first_name: firstName,
            last_name: lastName,
          });

          // After successful registration, log in automatically
          const loginResponse = await api.login(email, password);

          if (!loginResponse.user) {
            throw new Error('Login after signup failed.');
          }

          const user: User = {
            id: loginResponse.user.id,
            name: loginResponse.user.name,
            email: loginResponse.user.email,
            tier: loginResponse.user.tier,
          };

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            showAuthModal: false,
            savedPlaces: [],
            error: null,
          });

          onAuthenticatedSessionReady(get().loadSavedPlaces);

          // Execute redirect action if any
          const redirectAction = get().authRedirectAction;
          if (redirectAction) {
            redirectAction();
            set({ authRedirectAction: null });
          }

          return { success: true };
        } catch (error) {
          const message = error instanceof api.APIError
            ? error.message
            : 'Registration failed. Please try again.';
          set({ isLoading: false, error: message });
          return { success: false, error: message };
        }
      },

      logout: async () => {
        try {
          await api.logout();
        } catch (error) {
          console.error('Logout API error:', error);
        } finally {
          clearFieldCampaignsExploreEngagedFromSession();
          clearCampaignWorkspaceResumeIdFromSession();
          clearSessionRecentSearches();
          set({
            user: null,
            isAuthenticated: false,
            savedPlaces: [],
            error: null,
          });
        }
      },

      openAuthModal: (mode, redirectAction) => {
        set({
          showAuthModal: true,
          authModalMode: mode,
          authRedirectAction: redirectAction || null,
        });
      },

      closeAuthModal: () => {
        set({
          showAuthModal: false,
          authRedirectAction: null,
          requires2FA: false,
          twoFactorToken: null,
        });
      },

      setAuthModalMode: (mode) => set({ authModalMode: mode }),

      reset2FAState: () => set({ requires2FA: false, twoFactorToken: null, error: null }),

      addSavedPlace: (place) => {
        const newPlace: SavedPlace = {
          ...place,
          id: `saved_${Date.now()}`,
        };
        set((state) => ({
          savedPlaces: [...state.savedPlaces, newPlace],
        }));
      },

      removeSavedPlace: async (id) => {
        // Delete from server first
        try {
          await api.deleteSavedPlace(id);
        } catch (error) {
          console.error('Failed to delete saved place from server:', error);
          return;
        }
        // Only remove from local state after successful server delete
        set((state) => ({
          savedPlaces: state.savedPlaces.filter(p => p.id !== id),
        }));
      },

      updateSavedPlace: (id, updates) => {
        set((state) => ({
          savedPlaces: state.savedPlaces.map(p =>
            p.id === id ? { ...p, ...updates } : p
          ),
        }));
      },

      requireAuth: (action) => {
        const { isAuthenticated, openAuthModal } = get();
        if (isAuthenticated) {
          action();
          return true;
        } else {
          openAuthModal('login', action);
          return false;
        }
      },

      loadSavedPlaces: async () => {
        if (!get().isAuthenticated || !localStorage.getItem('auth_token')) return;
        try {
          const places = await api.getSavedPlaces();
          const transformedPlaces: SavedPlace[] = places.map(p => ({
            id: p.id,
            placeId: p.place_id,
            name: p.name,
            address: p.address,
            label: p.label,
            coordinates: { lat: p.coordinates.latitude, lng: p.coordinates.longitude },
          }));
          set({ savedPlaces: transformedPlaces });
        } catch {
          // Silently ignore — token may be expired/invalid during rehydration
        }
      },

      clearError: () => set({ error: null }),

      handleOAuthCallback: async () => {
        // Check URL for OAuth callback params
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const refreshToken = urlParams.get('refresh_token');
        const error = urlParams.get('error');
        const requires2FA = urlParams.get('requires_2fa');
        const twoFactorTokenParam = urlParams.get('two_factor_token');

        if (error) {
          set({ error: `OAuth error: ${error}` });
          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
          return false;
        }

        // Handle 2FA required from Google OAuth
        if (requires2FA === 'true' && twoFactorTokenParam) {
          set({
            requires2FA: true,
            twoFactorToken: twoFactorTokenParam,
            showAuthModal: true,
            authModalMode: 'login',
          });
          window.history.replaceState({}, '', window.location.pathname);
          return false;
        }

        if (token && refreshToken) {
          try {
            // Store tokens
            localStorage.setItem('auth_token', token);
            localStorage.setItem('refresh_token', refreshToken);

            // Fetch user profile with the new token
            const userProfile = await api.getCurrentUser();

            const user: User = {
              id: userProfile.id,
              name: userProfile.name || userProfile.email,
              email: userProfile.email,
              tier: userProfile.tier,
              two_factor_enabled: userProfile.two_factor_enabled,
            };

            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              showAuthModal: false,
              error: null,
            });

            // Load saved places
            onAuthenticatedSessionReady(get().loadSavedPlaces);

            // Execute redirect action if any
            const redirectAction = get().authRedirectAction;
            if (redirectAction) {
              redirectAction();
              set({ authRedirectAction: null });
            }

            // Clean URL - redirect to home page
            window.history.replaceState({}, '', '/');
            return true;
          } catch (err) {
            console.error('OAuth callback error:', err);
            set({ error: 'Failed to complete authentication' });
            window.history.replaceState({}, '', '/');
            return false;
          }
        }

        return false;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        savedPlaces: state.savedPlaces,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state?.isAuthenticated) return;
        const token = localStorage.getItem('auth_token');
        if (!token) {
          // Zustand says authenticated but no token in localStorage — force logout
          state.user = null;
          state.isAuthenticated = false;
          state.savedPlaces = [];
          return;
        }
        // Validate token with a lightweight call; apiRequest handles refresh internally
        const originalToken = token;
        api.getCurrentUser().then(() => {
          onAuthenticatedSessionReady(useAuthStore.getState().loadSavedPlaces);
        }).catch(() => {
          // Only force logout if the token hasn't changed (i.e. user hasn't logged in fresh)
          if (localStorage.getItem('auth_token') === originalToken) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            clearSessionRecentSearches();
            useAuthStore.setState({ user: null, isAuthenticated: false, savedPlaces: [] });
          }
        });
      },
    }
  )
);

if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __coordalAuthStore: typeof useAuthStore }).__coordalAuthStore = useAuthStore;
}
